import { useRef, useState, type DragEvent } from 'react';
import { Alert, Progress } from 'antd';
import { CheckCircleFilled, CloudUploadOutlined, LoadingOutlined } from '@ant-design/icons';
import { uploadDocument } from '@/api/dataroom';
import { normalizeError, type ErrorKind } from '@/api/errors';
import type { Task, TaskStatus } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { useTaskText } from './taskText';

/** A friendly hint for the file picker; the server (415) is authoritative on accepted types. */
const ACCEPT = 'image/*,application/pdf';

interface Receipt {
  name: string;
  hash: string;
}

function uploadErrorText(kind: ErrorKind, t: (k: string) => string): string {
  if (kind === 'TOO_LARGE') return t('upload.rejectedTooLarge');
  if (kind === 'BAD_TYPE') return t('upload.rejectedBadType');
  return t('error.inline');
}

/**
 * UPLOAD task: a large, phone-friendly drag-drop / tap-to-pick target. On success it shows the
 * server's contentHash back as a RECEIPT (the applicant's proof we received that exact file). 413/415
 * rejections are rendered as kind, plain-language guidance — never a raw error.
 */
export function UploadTask({
  task,
  onStatus,
}: {
  task: Task;
  onStatus: (status: TaskStatus) => void;
}) {
  const { t } = useI18n();
  const text = useTaskText(task);
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  const locked = task.status === 'ACCEPTED';

  async function handleFile(file: File | undefined) {
    if (!file || busy) return;
    setErrorText(null);
    setBusy(true);
    setProgress(0);
    try {
      const res = await uploadDocument(task.taskId, file, setProgress);
      setReceipt({ name: file.name, hash: res.contentHash });
      onStatus(res.status);
    } catch (err) {
      setErrorText(uploadErrorText(normalizeError(err).kind, t));
    } finally {
      setBusy(false);
    }
  }

  function onDrop(e: DragEvent<HTMLButtonElement>) {
    e.preventDefault();
    setDragging(false);
    if (locked || busy) return;
    void handleFile(e.dataTransfer.files?.[0]);
  }

  if (locked && receipt === null) {
    return (
      <Alert
        type="success"
        showIcon
        message={t('upload.receiptTitle')}
        description={t('taskStatus.ACCEPTED')}
      />
    );
  }

  return (
    <div className="dr-stack">
      {receipt && (
        <div className="dr-receipt">
          <CheckCircleFilled className="dr-receipt__icon" aria-hidden="true" />
          <div className="dr-receipt__body">
            <div className="dr-receipt__name">{t('upload.receiptName', { name: receipt.name })}</div>
            <div className="dr-receipt__hash">{t('upload.receiptHash', { hash: receipt.hash })}</div>
            <div className="dr-meta" style={{ marginTop: 4 }}>
              {t('upload.receiptHashNote')}
            </div>
          </div>
        </div>
      )}

      {!receipt && task.status === 'SUBMITTED' && (
        <Alert type="success" showIcon message={t('upload.receiptTitle')} />
      )}

      {errorText && (
        <Alert type="error" showIcon message={errorText} closable onClose={() => setErrorText(null)} />
      )}

      {!locked && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={task.ui?.accept?.join(',') ?? ACCEPT}
            hidden
            onChange={(e) => {
              void handleFile(e.target.files?.[0]);
              e.target.value = ''; // allow re-picking the same file
            }}
          />
          <button
            type="button"
            className={`dr-drop${dragging ? ' dr-drop--active' : ''}${busy ? ' dr-drop--busy' : ''}`}
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
          >
            {busy ? (
              <>
                <LoadingOutlined className="dr-drop__icon" aria-hidden="true" />
                <span className="dr-drop__title">{t('upload.uploading')}</span>
                <Progress percent={progress} size="small" style={{ maxWidth: 220 }} />
              </>
            ) : (
              <>
                <CloudUploadOutlined className="dr-drop__icon" aria-hidden="true" />
                <span className="dr-drop__title">
                  {receipt || task.status === 'SUBMITTED' ? t('upload.replace') : text('dropTitle', 'upload.dropTitle')}
                </span>
                <span className="dr-drop__hint">{text('dropHint', 'upload.dropHint')}</span>
              </>
            )}
          </button>
        </>
      )}
    </div>
  );
}
